// IMEI/Serial Bank utility functions

/**
 * Validates IMEI number format
 * @param {string} imei - IMEI number to validate
 * @returns {boolean} - True if valid IMEI format
 */
export const validateImeiFormat = (imei) => {
  if (!imei || typeof imei !== 'string') return false;
  
  // Remove any non-digit characters
  const cleanImei = imei.replace(/\D/g, '');
  
  // IMEI should be 15 digits
  if (cleanImei.length !== 15) return false;
  
  // Check if all characters are digits
  if (!/^\d{15}$/.test(cleanImei)) return false;
  
  return true;
};

/**
 * Validates serial number format (more flexible than IMEI)
 * @param {string} serial - Serial number to validate
 * @returns {boolean} - True if valid serial format
 */
export const validateSerialFormat = (serial) => {
  if (!serial || typeof serial !== 'string') return false;
  
  // Serial numbers can be alphanumeric, 6-20 characters
  const cleanSerial = serial.trim();
  return cleanSerial.length >= 6 && cleanSerial.length <= 20 && /^[A-Za-z0-9]+$/.test(cleanSerial);
};

/**
 * Formats IMEI number for display
 * @param {string} imei - IMEI number to format
 * @returns {string} - Formatted IMEI
 */
export const formatImei = (imei) => {
  if (!imei) return '';
  
  const cleanImei = imei.replace(/\D/g, '');
  if (cleanImei.length !== 15) return imei;
  
  // Format as XXXXX-XXXXXX-XXXXX
  return `${cleanImei.slice(0, 5)}-${cleanImei.slice(5, 11)}-${cleanImei.slice(11)}`;
};

/**
 * Formats date for IMEI records
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date (MM/DD/YYYY)
 */
export const formatImeiDate = (date) => {
  if (!date) return '-';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return date || '-';
    
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return date || '-';
  }
};

/**
 * Parses CSV/TXT file content for bulk import
 * @param {string} content - File content
 * @returns {Array} - Array of parsed IMEI/serial numbers
 */
export const parseBulkImport = (content) => {
  if (!content || typeof content !== 'string') return [];
  
  const lines = content
    .split(/\r?\n|,/)
    .map(line => line.trim())
    .filter(line => line.length > 0);
  
  return lines;
};

/**
 * Validates bulk import data
 * @param {Array} data - Array of IMEI/serial numbers
 * @returns {Object} - Validation result with valid, invalid, and duplicate arrays
 */
export const validateBulkImport = (data) => {
  const valid = [];
  const invalid = [];
  const duplicates = new Set();
  const seen = new Set();
  
  data.forEach((item, index) => {
    const trimmed = item.trim();
    
    if (!trimmed) {
      invalid.push({ index, value: item, reason: 'Empty' });
      return;
    }
    
    // Check for duplicates within the import
    if (seen.has(trimmed)) {
      duplicates.add(trimmed);
      return;
    }
    seen.add(trimmed);
    
    // Validate format (IMEI or serial)
    const isImei = validateImeiFormat(trimmed);
    const isSerial = validateSerialFormat(trimmed);
    
    if (isImei || isSerial) {
      valid.push({ index, value: trimmed, type: isImei ? 'imei' : 'serial' });
    } else {
      invalid.push({ index, value: item, reason: 'Invalid format' });
    }
  });
  
  return {
    valid,
    invalid,
    duplicates: Array.from(duplicates),
    summary: {
      total: data.length,
      valid: valid.length,
      invalid: invalid.length,
      duplicates: duplicates.size
    }
  };
};

/**
 * Generates CSV content for export
 * @param {Array} data - Array of IMEI/serial numbers
 * @param {Object} recordInfo - Record information
 * @returns {string} - CSV content
 */
export const generateCsvExport = (data, recordInfo = {}) => {
  const headers = ['Record No.', 'Date', 'Supplier', 'IMEI/Serial', 'Type'];
  const rows = [headers];
  
  data.forEach((item, index) => {
    const row = [
      recordInfo.record_no || (index + 1).toString().padStart(2, '0'),
      formatImeiDate(recordInfo.date_recorded),
      recordInfo.supplier?.name || '',
      item.imei || item.serial || item,
      item.type || (validateImeiFormat(item.imei || item) ? 'IMEI' : 'Serial')
    ];
    rows.push(row);
  });
  
  return rows.map(row => row.join(',')).join('\n');
};

/**
 * Generates Excel file for export
 * @param {Array} data - Array of IMEI/serial numbers
 * @param {Object} recordInfo - Record information
 * @returns {Blob} - Excel file blob
 */
export const generateExcelExport = (data, recordInfo = {}) => {
  // This would require xlsx library
  // For now, return CSV format
  const csvContent = generateCsvExport(data, recordInfo);
  return new Blob([csvContent], { type: 'text/csv' });
};

/**
 * Debounce function for search
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Generates record number with padding
 * @param {number} number - Record number
 * @param {number} padding - Padding length (default: 2)
 * @returns {string} - Padded record number
 */
export const generateRecordNumber = (number, padding = 2) => {
  return String(number).padStart(padding, '0');
};

/**
 * Calculates pagination info
 * @param {number} total - Total items
 * @param {number} page - Current page
 * @param {number} pageSize - Items per page
 * @returns {Object} - Pagination info
 */
export const calculatePagination = (total, page, pageSize) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);
  
  return {
    totalPages,
    startItem,
    endItem,
    hasNext: page < totalPages,
    hasPrev: page > 1
  };
};

/**
 * Filters records based on search criteria
 * @param {Array} records - Records to filter
 * @param {string} searchTerm - Search term
 * @param {string} supplierId - Supplier ID filter
 * @returns {Array} - Filtered records
 */
export const filterRecords = (records, searchTerm = '', supplierId = '') => {
  let filtered = [...records];
  
  // Filter by supplier
  if (supplierId) {
    filtered = filtered.filter(record => 
      String(record.supplier?.id) === String(supplierId)
    );
  }
  
  // Filter by search term
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(record =>
      (record.record_no || '').toLowerCase().includes(term) ||
      (record.notes || '').toLowerCase().includes(term) ||
      (record.supplier?.name || '').toLowerCase().includes(term)
    );
  }
  
  return filtered;
};

/**
 * Sorts records by specified field
 * @param {Array} records - Records to sort
 * @param {string} field - Field to sort by
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted records
 */
export const sortRecords = (records, field = 'date_recorded', direction = 'desc') => {
  return [...records].sort((a, b) => {
    let aVal = a[field];
    let bVal = b[field];
    
    // Handle date fields
    if (field === 'date_recorded') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    // Handle string fields
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (direction === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });
};

const imeiHelpers = {
  validateImeiFormat,
  validateSerialFormat,
  formatImei,
  formatImeiDate,
  parseBulkImport,
  validateBulkImport,
  generateCsvExport,
  generateExcelExport,
  debounce,
  generateRecordNumber,
  calculatePagination,
  filterRecords,
  sortRecords
};

export default imeiHelpers;
